import { Components, Theme, alpha } from '@mui/material'
import { colors } from '../constants'

declare module '@mui/material/Button' {
  // extending the Button Variants with our custom variant options
  export interface ButtonPropsVariantOverrides {
    square: true
  }
  // extending the Button Colors with our custom color options
  export interface ButtonPropsColorOverrides {
    primary: true
    secondary: true
    tertiary: true
  }
}

const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    // The default props to change
    disableElevation: true,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: '2px', // Customize the border radius here
      padding: '18px 28px',
      height: '3rem',
      transition: theme.transitions.create(
        ['background-color', 'box-shadow', 'border', 'opacity'],
        {
          duration: theme.transitions.duration.short,
        },
      ),
    }),
  },
  variants: [
    {
      // default styles for button with variant 'contained'
      props: { variant: 'contained' },
      style: ({ theme }) => ({
        minWidth: theme.spacing(20),
        height: theme.spacing(5),
        '& .MuiTouchRipple-child': {
          backgroundColor: 'white',
          color: 'white',
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
        },
        '&:focus': {
          backgroundColor: theme.palette.primary.dark,
          borderColor: theme.palette.primary.dark,
          outline: '1px solid #202020',
          outlineOffset: '1px',
        },
      }),
    },
    {
      // default styles for button with variant 'outlined'
      props: { variant: 'outlined' },
      style: ({ theme }) => ({
        minWidth: theme.spacing(20),
        height: theme.spacing(5),
        borderColor: theme.palette.grey[600],
        backgroundClip: 'padding-box',
        '& .MuiTouchRipple-child': {
          backgroundColor: theme.palette.grey[500],
          color: theme.palette.grey[500],
        },
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          borderColor: theme.palette.action.hover,
        },
        '&:focus': {
          outline: '5px auto -webkit-focus-ring-color',
        },
      }),
    },
    {
      // default styles for button with variant 'square'
      props: { variant: 'square' },
      style: {
        backgroundColor: colors.teal[300],
        color: colors.black,
      },
    },
    {
      // default styles for button with variant 'square' and color 'primary'
      props: { variant: 'square', color: 'secondary' },
      style: {
        background: 'none',
        border: `1px solid ${colors.grey[700]}`,
        '&:hover': {
          border: `1px solid ${colors.grey[900]}`,
          background: alpha(colors.grey[900], 0.06),
        },
      },
    },
    {
      // default styles for a disabled button with variant 'square' and color 'primary'
      props: { variant: 'square', color: 'primary', disabled: true },
      style: {
        backgroundColor: alpha(colors.grey[900], 0.38),
        color: colors.black,
      },
    },
    {
      // default styles for a disabled button with variant 'square' and color 'secondary'
      props: { variant: 'square', color: 'secondary', disabled: true },
      style: {
        background: 'none',
        border: `1px solid ${colors.grey[700]}`,
        opacity: 0.38,
      },
    },
    {
      // default styles for a disabled button with variant 'square' and color 'tertiary'
      props: { variant: 'square', color: 'tertiary' },
      style: {
        backgroundColor: colors.grey[900],
        '&:hover': {
          backgroundColor: colors.grey[900],
          opacity: 0.8,
        },
      },
    },
    {
      // default styles for button with variant 'text' and color 'primary'
      props: { variant: 'text', color: 'primary' },
      style: {
        background: 'none',
        color: colors.teal[800],
        '&:hover': {
          color: colors.teal[300],
        },
        '&:hover, $focusVisible': {
          color: colors.teal[300],
        },
      },
    },
    {
      // default styles for button with variant 'text' and color 'secondary'
      props: { variant: 'text', color: 'secondary' },
      style: {
        background: 'none',
        color: colors.grey[700],
        '&:hover': {
          color: colors.grey[900],
        },
        '&:hover, $focusVisible': {
          color: colors.grey[900],
        },
      },
    },
    {
      // default styles for a disabled button with variant 'text'
      props: { variant: 'text', disabled: true },
      style: {
        background: 'none',
        color: colors.grey[900],
      },
    },
    {
      // default styles for small buttons
      props: { size: 'small' },
      style: {
        padding: '14px 24px',
        $outlined: {
          padding: '13px 24px',
        },
      },
    },
    {
      // default styles for medium buttons
      props: { size: 'medium' },
      style: {
        padding: '18px 28px',
        $outlined: {
          padding: '17px 28px',
        },
      },
    },
    {
      // default styles for large buttons
      props: { size: 'large' },
      style: {
        padding: '22px 28px',
        $outlined: {
          padding: '21px 28px',
        },
      },
    },
  ],
}

export { MuiButton }
